import { ApiResult, HttpClient } from "../http.js";
import { BilagApprovalStatus, BilagCategory, BilagDto, BilagSyncStatus, PagedBilag } from "../types/bilag.js";

export interface ListBilagQuery {
  projectId?: string;
}

export interface ListBilagPagedQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  projectId?: string;
  /** Approval-status token; filtered server-side. */
  status?: BilagApprovalStatus;
}

/**
 * The caller is responsible for building the FormData on their platform
 * (web: use File/Blob; React Native: use the RN FormData API with a
 * `{ uri, name, type }` object). The shared lib never references `Blob`,
 * `File`, or constructs `FormData` so that it compiles in envs without
 * the DOM lib (e.g. rn-hooks).
 */
export interface CreateBilagPayload {
  file: FormData;
}

export interface UpdateBilagPayload {
  description: string;
  supplier: string;
  invoiceNumber?: string | null;
  /** ISO date string, e.g. "2026-06-04" */
  receiptDate: string;
  amountExclVat: number;
  vatAmount: number;
  totalAmount: number;
  currency: string;
  category: BilagCategory;
  accountNumber?: number | null;
  linkedMaterialIds?: string[] | null;
}

export interface RejectBilagPayload {
  reason?: string | null;
}

export interface UpdateBilagSyncStatusPayload {
  syncStatus: BilagSyncStatus;
  /** ISO timestamp, e.g. "2026-06-04T10:00:00Z" */
  syncedAt?: string | null;
  syncError?: string | null;
  dineroVoucherId?: string | null;
  economicVoucherNumber?: number | null;
}

export class BilagApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListBilagQuery = {}): Promise<ApiResult<BilagDto[]>> {
    return this.http.get<BilagDto[]>("/api/bilag", { query: { ...query } });
  }

  /**
   * Server-paged, server-searched sibling of {@link list}. Corresponds to
   * GET /api/bilag/paged. `search` matches description, supplier, project title or
   * invoice number; newest created first, as {@link list}.
   */
  listPaged(query: ListBilagPagedQuery = {}): Promise<ApiResult<PagedBilag>> {
    return this.http.get<PagedBilag>("/api/bilag/paged", { query: { ...query } });
  }

  /** Distinct supplier names across the org's bilag, A→Z. GET /api/bilag/suppliers. */
  listSuppliers(): Promise<ApiResult<string[]>> {
    return this.http.get<string[]>("/api/bilag/suppliers");
  }

  get(id: string): Promise<ApiResult<BilagDto>> {
    return this.http.get<BilagDto>(`/api/bilag/${encodeURIComponent(id)}`);
  }

  /**
   * Upload a new bilag. The caller must build the `FormData` containing all
   * required fields (file, projectId, projectTitle, description, supplier,
   * receiptDate, amountExclVat, vatAmount, totalAmount, currency, category,
   * and optional invoiceNumber, accountNumber, linkedMaterialIds, id) before
   * calling this method.
   */
  create(payload: CreateBilagPayload): Promise<ApiResult<BilagDto>> {
    return this.http.post<BilagDto>("/api/bilag", { body: payload.file });
  }

  update(id: string, payload: UpdateBilagPayload): Promise<ApiResult<BilagDto>> {
    return this.http.patch<BilagDto>(`/api/bilag/${encodeURIComponent(id)}`, { body: payload });
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/bilag/${encodeURIComponent(id)}`);
  }

  approve(id: string): Promise<ApiResult<BilagDto>> {
    return this.http.post<BilagDto>(`/api/bilag/${encodeURIComponent(id)}/approve`);
  }

  reject(id: string, payload: RejectBilagPayload = {}): Promise<ApiResult<BilagDto>> {
    return this.http.post<BilagDto>(`/api/bilag/${encodeURIComponent(id)}/reject`, { body: payload });
  }

  submit(id: string): Promise<ApiResult<BilagDto>> {
    return this.http.post<BilagDto>(`/api/bilag/${encodeURIComponent(id)}/submit`);
  }

  /**
   * Persist the outcome of an ERP (Dinero / e-conomic) sync attempt so a
   * successfully-pushed bilag is not pushed again (duplicate-voucher guard).
   */
  updateSyncStatus(
    id: string,
    payload: UpdateBilagSyncStatusPayload,
  ): Promise<ApiResult<BilagDto>> {
    return this.http.post<BilagDto>(
      `/api/bilag/${encodeURIComponent(id)}/sync-status`,
      { body: payload },
    );
  }
}

// Re-export status type for consumers who only import from this module
export type { BilagApprovalStatus };
